/**
 * Payments API client — Cashfree-first under the new abstraction.
 *
 * Talks to /api/payments/* on the backend. The backend routes per
 * currency / kind to the right provider (Cashfree today, Stripe when
 * invite lands). Callers don't pick a provider — the backend does.
 */
import api from "./axios";

export const redeemTeamCode = (code) =>
  api.post("/api/payments/redeem-team-code/", { code });

/**
 * Start a one-time wallet topup checkout.
 * @param {string} packCode  — TopupPack.code (e.g. "TOPUP_99")
 * @param {string} returnUrl — where to send the customer after Drop-in
 * @returns {Promise<{provider, order_id, payment_session_id, hosted_url,
 *                    amount_paise, amount_inr, credits, pack_code}>}
 */
export const checkoutTopup = (packCode, returnUrl) =>
  api.post("/api/payments/checkout/topup/", {
    pack_code: packCode,
    return_url: returnUrl,
  });

/**
 * Start a recurring subscription checkout (main plan OR storage add-on).
 * @param {string} planCode   — Plan.code
 * @param {string} returnUrl  — post-mandate redirect
 * @param {object} opts
 *   - auth_method: "upi" (default — UPI Mandate only on the auth page),
 *                  "any" / null (full multi-method page),
 *                  "card" / "netbanking" / "credit_card" / "debit_card"
 *
 * T209: UPI is now the default because Indian B2C operators prefer
 * UPI Autopay over card mandates (lower cycle-failure rate, no card-
 * expiry surprises). Callers needing the legacy multi-method page can
 * pass {auth_method: "any"}.
 */
export const checkoutSubscription = (planCode, returnUrl, opts = {}) =>
  api.post("/api/payments/checkout/subscription/", {
    plan_code: planCode,
    return_url: returnUrl,
    auth_method: opts.auth_method ?? "upi",
  });

/**
 * Poll Cashfree's authoritative status for an order. Use after the
 * Drop-in closes if the webhook is slow / hasn't credited the wallet
 * yet — UI can re-fetch wallet balance once status === "SUCCESS".
 */
export const getCheckoutStatus = (orderId) =>
  api.get(`/api/payments/checkout/status/${encodeURIComponent(orderId)}/`);

// 2026-06-20 — Customer-facing billing.

export const getBillingProfile = () =>
  api.get("/api/payments/billing-profile/me/");

export const upsertBillingProfile = (data) =>
  api.put("/api/payments/billing-profile/me/", data);

export const listMyInvoices = () =>
  api.get("/api/payments/invoices/me/");

export const getMyInvoicePdf = (invoiceId) =>
  api.get(`/api/payments/invoices/${encodeURIComponent(invoiceId)}/pdf/`);
